import React, { useState, useEffect } from "react";
import Button from "@/components/ui/Button";
import Dropdown from "@/components/ui/DropDown";
import { Settings } from "lucide-react";
import CampaignDetailWrapper from "./campaign-detail-wrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import useCampaignStore from "@/pages/Campaign/store/campaignStore";
import useGlobalStore from "@/store/globalStore";
import { useShallow } from "zustand/react/shallow";

const CampaignSummary: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("id");

  const {
    campaignSummary,
    assetsTableData,
    assetsTableMeta,
    getCampaignSummary,
    getAssetsTable,
  } = useCampaignStore(
    useShallow((state) => ({
      campaignSummary: state.campaignSummary,
      assetsTableData: state.assetsTableData,
      assetsTableMeta: state.assetsTableMeta,
      getCampaignSummary: state.getCampaignSummary,
      getAssetsTable: state.getAssetsTable,
    })),
  );

  const setError = useGlobalStore((state) => state.setError);

  const [selectedFlight, setSelectedFlight] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [sortColumn, setSortColumn] = useState("publisher");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch campaign summary on mount
  useEffect(() => {
    if (campaignId) {
      fetchCampaignSummary();
    }
  }, [campaignId]);

  // Fetch assets table when flight changes
  useEffect(() => {
    if (campaignId && selectedFlight) {
      fetchAssetsTable();
    }
  }, [campaignId, selectedFlight, sortColumn, sortDirection, currentPage]);

  const fetchCampaignSummary = async () => {
    try {
      const data = await getCampaignSummary(campaignId!);
      // Set first flight as default
      if (data.flights?.length > 0) {
        setSelectedFlight(data.flights[0]?.id);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const fetchAssetsTable = async () => {
    try {
      await getAssetsTable(
        campaignId!,
        selectedFlight,
        sortColumn,
        sortDirection,
        currentPage,
      );
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const flightOptions =
    campaignSummary?.flights.map((flight) => ({
      title: `Flight ${flight.flight_number}`,
      value: flight.id,
    })) || [];

  const languageOptions = [
    { title: "All", value: "All" },
    // You can add language filtering if needed
  ];

  const goPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((page) => page - 1);
    }
  };

  const goNext = () => {
    if (assetsTableMeta && currentPage < assetsTableMeta.last_page) {
      setCurrentPage((page) => page + 1);
    }
  };

  if (!campaignSummary) {
    return (
      <CampaignDetailWrapper>
        <div className="text-center py-8">Loading campaign data...</div>
      </CampaignDetailWrapper>
    );
  }

  return (
    <CampaignDetailWrapper>
      <div>
        {/* Flight Tabs and Language Filter */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            {/* Flight Tabs */}
            <div className="flex rounded-full overflow-hidden border border-[#EA33C0]">
              {flightOptions.map((flight, index) => (
                <button
                  key={flight.value}
                  onClick={() => {
                    setSelectedFlight(flight.value);
                    setCurrentPage(1);
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors ${
                    index < flightOptions.length - 1
                      ? "border-r border-[#EA33C0]"
                      : ""
                  } ${
                    selectedFlight === flight.value
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {flight.title}
                </button>
              ))}
            </div>

            {/* Language Dropdown */}
            <div className="flex items-center gap-2 ml-4">
              <span className="font-medium text-gray-700">Language</span>
              <Dropdown
                options={languageOptions}
                selected={selectedLanguage}
                setSelected={(value) => setSelectedLanguage(value)}
                className="min-w-[120px]"
              />
            </div>
          </div>

          {/* Edit Assets Button */}
          <div className="flex">
            <Button
              type="secondary"
              leadingIcon={<Settings size={18} />}
              className="mr-3"
              onClick={() => {
                navigate(`/campaign/assets?id=${campaignId}`);
              }}
            >
              Edit Assets
            </Button>
            <Button
              type="secondary"
              leadingIcon={<Settings size={18} />}
              onClick={() => {
                navigate(`/campaign/flight?id=${campaignId}`);
              }}
            >
              Edit Flight
            </Button>

            {/* Button to go for the Dashboard added by Dvip */}
            <Button
              type="primary"
              className="bg-[#EA33C0] text-white hover:bg-[#d629a8]"
              onClick={() => {
                navigate(`/campaign/${campaignId}`);
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-12 border-b border-gray-300 pb-3 mb-2 text-sm font-semibold text-gray-700 select-none">
          <div
            className="col-span-1 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("language")}
          >
            Language
            <svg
              className={`w-4 h-4 ${
                sortColumn === "language" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "language" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div
            className="col-span-1 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("objective")}
          >
            Objective
            <svg
              className={`w-4 h-4 ${
                sortColumn === "objective" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "objective" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("region")}
          >
            Region
            <svg
              className={`w-4 h-4 ${
                sortColumn === "region" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "region" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div
            className="col-span-1 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("category")}
          >
            Category
            <svg
              className={`w-4 h-4 ${
                sortColumn === "category" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "category" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("publisher")}
          >
            Publisher
            <svg
              className={`w-4 h-4 ${
                sortColumn === "publisher" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "publisher" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("ad_type")}
          >
            Ad Type
            <svg
              className={`w-4 h-4 ${
                sortColumn === "ad_type" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "ad_type" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer gap-1"
            onClick={() => handleSort("due_date")}
          >
            Due Date
            <svg
              className={`w-4 h-4 ${
                sortColumn === "due_date" ? "text-pink-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                transform:
                  sortColumn === "due_date" && sortDirection === "desc"
                    ? "rotate(180deg)"
                    : "none",
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div className="col-span-1 flex items-center cursor-pointer gap-1">
            Status
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-200">
          {assetsTableData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No assets found for this flight
            </div>
          ) : (
            assetsTableData.map((asset) => (
              <div
                key={asset.id}
                className="grid grid-cols-12 py-4 text-sm items-center"
              >
                <div className="col-span-1">{asset.language}</div>
                <div className="col-span-1">{asset.objective}</div>
                <div className="col-span-2">{asset.region}</div>
                <div className="col-span-1">{asset.category}</div>
                <div className="col-span-2">{asset.publisher}</div>
                <div className="col-span-2">{asset.ad_type}</div>
                <div className="col-span-2">{asset.due_date}</div>
                <div className="col-span-1">
                  <button className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path
                        fillRule="evenodd"
                        d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {assetsTableMeta && (
          <div className="flex justify-end items-center mt-6 text-sm">
            <div className="flex items-center space-x-4">
              <Button
                type="secondary"
                onClick={goPrevious}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span>
                Page {currentPage} of {assetsTableMeta.last_page}
              </span>
              <Button
                type="secondary"
                onClick={goNext}
                disabled={
                  currentPage === assetsTableMeta.last_page ||
                  assetsTableMeta.last_page === 0
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <Button
          type="primary"
          onClick={() => {
            navigate("/dashboard");
          }}
        >
          Submit
        </Button>
      </div>
    </CampaignDetailWrapper>
  );
};

export default CampaignSummary;
